/*
 * Cart hold client. Safe to evaluate more than once (HAZ-139): the whole file is
 * an IIFE behind an init guard, config lives on window (no top-level const/let),
 * and the only listener (visibilitychange) is bound once on document.
 * The functions other theme code calls are exported on window at the end.
 */
(function () {
  if (window.__sbfaReserveInit) return;
  window.__sbfaReserveInit = true;

  window.SBFA_RESERVE_API_URL = window.SBFA_RESERVE_API_URL || "https://sbfa-functions.vercel.app/api/admin";
  const RESERVE_API_URL = window.SBFA_RESERVE_API_URL;
  // Cache key and expiration
  const cacheKey = "inCartProductIds";
  const cacheExpiryKey = "inCartProductIdsExpiry";
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
  const pendingRequestsKey = "pendingReservations"; // Track ongoing reservation requests

  // Prevent race conditions with a mutex-like pattern
  let ongoingReservations = new Set();

  // TODO(HAZ-69): delete this token-less fallback in a later cleanup, after the
  // theme is published against a backend that always issues hold sessions.
  const HOLD_TOKEN_KEY = "sbfaHoldToken";
  const HOLD_SESSION_KEY = "sbfaHoldSessionId";
  let holdSessionLegacy = false;
  // True when the last hold list fetch failed. Only a server answer may mark items expired.
  let holdApiDown = false;
  const CHECKOUT_VERIFY_TIMEOUT_MS = 4000;

  // Short timeout on every hold-service call so a slow or dead service never hangs
  // add to cart or checkout (fail open).
  const HOLD_FETCH_TIMEOUT_MS = 3500;
  function fetchWithTimeout(url, opts) {
    const ctrl = typeof AbortController === "function" ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), HOLD_FETCH_TIMEOUT_MS) : null;
    return fetch(url, Object.assign({}, opts || {}, ctrl ? { signal: ctrl.signal } : {}))
      .finally(() => { if (timer) clearTimeout(timer); });
  }

  // ZZ TEST products (Shopify tag zz-test-hidden) are test carts only: no reservation,
  // hold, timer, expiry or pre-checkout check. The theme marks their buttons with
  // data-sbfa-test and renders no countdown for their cart lines. Real products keep
  // the full hold behavior.
  // Emergency kill switch (theme setting sbfa_holds_off, rendered as window.SBFA_HOLDS_OFF
  // in layout/theme.liquid): when on, no hold is created, checked, shown or verified, so a
  // stalled or wrong hold service can never block add to cart or checkout.
  function holdsOff() { return window.SBFA_HOLDS_OFF === true; }

  function isTestProduct(productId) {
    if (holdsOff()) return true;
    if (!productId) return false;
    return !!document.querySelector('[data-product-id="' + productId + '"][data-sbfa-test]');
  }

  function holdSessionUrl() {
    return RESERVE_API_URL.replace(/\/$/, "") + "/session";
  }

  async function ensureHoldSession(forceNew) {
    if (forceNew) {
      localStorage.removeItem(HOLD_TOKEN_KEY);
      localStorage.removeItem(HOLD_SESSION_KEY);
      holdSessionLegacy = false;
    } else if (holdSessionLegacy) {
      return { token: null, sessionId: await getCookie("session_id"), legacy: true };
    } else {
      const token = localStorage.getItem(HOLD_TOKEN_KEY);
      const sessionId = localStorage.getItem(HOLD_SESSION_KEY);
      if (token && sessionId) {
        document.cookie = "session_id=" + sessionId + "; path=/; SameSite=Lax";
        return { token, sessionId, legacy: false };
      }
    }

    const response = await fetchWithTimeout(holdSessionUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (response.status === 404) {
      holdSessionLegacy = true;
      return { token: null, sessionId: await getCookie("session_id"), legacy: true };
    }
    if (!response.ok) throw new Error("Failed to start hold session");
    const data = await response.json();
    if (!data || !data.token || !data.sessionId) throw new Error("Hold session response missing token");
    holdSessionLegacy = false;
    localStorage.setItem(HOLD_TOKEN_KEY, data.token);
    localStorage.setItem(HOLD_SESSION_KEY, data.sessionId);
    document.cookie = "session_id=" + data.sessionId + "; path=/; SameSite=Lax";
    return { token: data.token, sessionId: data.sessionId, legacy: false };
  }

  async function holdFetch(url, config, allowRetry) {
    if (allowRetry === undefined) allowRetry = true;
    const session = await ensureHoldSession(false);
    const built = typeof config === "function" ? config(session) : (config || {});
    const headers = Object.assign({}, built.headers || {});
    if (session.token) headers.Authorization = "Bearer " + session.token;
    else delete headers.Authorization;
    const response = await fetchWithTimeout(url, Object.assign({}, built, { headers }));
    if (response.status === 401 && session.token && allowRetry) {
      await ensureHoldSession(true);
      return holdFetch(url, config, false);
    }
    return { response, session };
  }

  function normalizeHoldList(data, sessionId) {
    if (Array.isArray(data)) {
      return data.map((item) => ({
        productId: item.productId,
        sessionId: item.sessionId || null,
        expiresAt: item.expiresAt || null,
        mine: item.mine === true || (!!item.sessionId && item.sessionId === sessionId),
      }));
    }
    if (data && typeof data === "object") {
      const holds = Array.isArray(data.holds) ? data.holds : [];
      const reserved = Array.isArray(data.reservedProductIds) ? data.reservedProductIds : [];
      const ownIds = new Set(holds.map((item) => item.productId));
      const own = holds.map((item) => ({
        productId: item.productId,
        sessionId: item.sessionId || sessionId,
        expiresAt: item.expiresAt || null,
        mine: true,
      }));
      const foreign = reserved.filter((id) => !ownIds.has(id)).map((productId) => ({
        productId,
        sessionId: null,
        expiresAt: null,
        mine: false,
      }));
      return own.concat(foreign);
    }
    return [];
  }

  function isOwnHold(item, sessionId) {
    return item.mine === true || (!!item.sessionId && item.sessionId === sessionId);
  }

  // Fetches ids and updates cache if expired -> returns all ids
  async function fetchInCartIds(forceFetch = false) {
    if (holdsOff()) return [];
    try {
      const cachedData = JSON.parse(localStorage.getItem(cacheKey));
      const cacheExpiry = localStorage.getItem(cacheExpiryKey);

      // Check if cache exists and is still valid
      if (!forceFetch && cachedData && cacheExpiry && Date.now() < parseInt(cacheExpiry)) {
        console.log("Using cached data");
        return cachedData;
      }

      console.log("Refreshing cache");
      await refreshCache();
      return JSON.parse(localStorage.getItem(cacheKey)) || [];
    } catch (error) {
      console.error("Error fetching in-cart IDs:", error);
      return [];
    }
  }

  // Updates cache with the latest in-cart items and sets a 5-minute expiration
  async function refreshCache() {
    if (holdsOff()) { holdApiDown = false; return; }
    try {
      const { response, session } = await holdFetch(RESERVE_API_URL, { method: "GET" });
      if (!response.ok) {
        throw new Error("Failed to fetch in-cart product IDs");
      }

      const data = await response.json();
      const normalized = normalizeHoldList(data, session.sessionId);

      localStorage.setItem(cacheKey, JSON.stringify(normalized));
      localStorage.setItem(cacheExpiryKey, (Date.now() + CACHE_TTL).toString());
      holdApiDown = false;
      console.log("Cache refreshed with latest data");

    } catch (error) {
      // Hold server unreachable or erroring: remember it so the cart does not
      // treat an empty/stale cache as "your hold expired" (HAZ-139, fail open).
      holdApiDown = true;
      console.error("Error refreshing cache:", error);
    }
  }

  // returns `true` or `false` - always fetches fresh data to prevent race conditions
  async function isInCart(itemId) {
    if (isTestProduct(itemId)) return false;
    const data = await fetchInCartIds(true); // Force fresh fetch for accuracy
    const inCartProductIds = data.map((item) => item.productId);
    return inCartProductIds.includes(itemId);
  }


  function disableAddToCartFromProductPage(itemId) {
    if (holdsOff()) return;
    const addToCartButton = document.querySelector(`[data-product-id="${itemId}"].add-to-cart-button`);
    if (!addToCartButton) { 
      console.log("no add to cart button found")
      return
    }
    addToCartButton.setAttribute('disabled', true);
    addToCartButton.textContent = "in another cart";
  }

  async function disableAddToCartFromCollectionPage(enableAvailableButtons = true) {
    try {
      // Step 1: Fetch all in-cart product IDs (force fresh fetch for accuracy)
      const inCartProductIds = (await fetchInCartIds(true)).map((item) => item.productId);
      console.log('Reserved product IDs:', inCartProductIds);

      // Step 2: Get all product buttons on the page
      const allButtons = document.querySelectorAll('.add-to-cart-button, .quick-add__submit');

      allButtons.forEach((button) => {
        const productId = button.dataset.productId;
        if (!productId) return;

        const isReserved = !holdsOff() && !button.hasAttribute('data-sbfa-test') && inCartProductIds.includes(productId);

        if (isReserved) {
          // Product is reserved - disable it
          button.setAttribute('disabled', true);
          button.setAttribute('aria-disabled', true);

          // Hide spinner, show text
          const spinner = button.querySelector('.loading__spinner');
          const span = button.querySelector('span');
          if (spinner) spinner.classList.add('hidden');
          if (span) {
            span.style.display = '';
            span.textContent = 'in another cart';
          } else {
            button.textContent = 'in another cart';
          }
        } else if (enableAvailableButtons) {
          // Product is available - enable it if it was checking availability
          if (button.dataset.checkingAvailability) {
            button.removeAttribute('disabled');
            button.removeAttribute('aria-disabled');
            button.removeAttribute('data-checking-availability');

            // Hide spinner, show text
            const spinner = button.querySelector('.loading__spinner');
            const span = button.querySelector('span');
            if (spinner) spinner.classList.add('hidden');
            if (span && button.dataset.originalText) {
              span.style.display = '';
              span.textContent = button.dataset.originalText;
            }
          }
        }
      });

      return true;
    } catch (error) {
      console.error('Error updating Add to Cart buttons:', error);

      // On error, enable all buttons that were checking availability to avoid blocking purchases
      if (enableAvailableButtons) {
        const checkingButtons = document.querySelectorAll('[data-checking-availability]');
        checkingButtons.forEach(button => {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-disabled');
          button.removeAttribute('data-checking-availability');

          // Hide spinner, show text
          const spinner = button.querySelector('.loading__spinner');
          const span = button.querySelector('span');
          if (spinner) spinner.classList.add('hidden');
          if (span && button.dataset.originalText) {
            span.style.display = '';
            span.textContent = button.dataset.originalText;
          }
        });
      }

      return false;
    }
  }

  async function reserveProduct(productId) {
    if (isTestProduct(productId)) return { success: true, test: true };
    const session = await ensureHoldSession(false);
    const sessionId = session.sessionId;
    if (!sessionId || !productId) {
      return { success: false, error: 'Missing session ID or product ID' };
    }

    // Prevent duplicate simultaneous reservations for the same product
    if (ongoingReservations.has(productId)) {
      console.warn(`Reservation already in progress for product ${productId}`);
      return { success: false, error: 'Reservation already in progress' };
    }

    // Mark this product as being reserved
    ongoingReservations.add(productId);

    try {
      // CRITICAL: Verify product is not already in ANY cart before attempting reservation
      const currentInCart = await fetchInCartIds(true); // Force fresh fetch
      const inCartProductIds = currentInCart.map((item) => item.productId);

      if (inCartProductIds.includes(productId)) {
        console.warn(`Product ${productId} is already in a cart`);
        return { success: false, error: 'Product already reserved by another customer' };
      }

      console.log("Reserving product:", productId, "for session:", sessionId);

      const { response } = await holdFetch(RESERVE_API_URL, (current) => ({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: current.sessionId,
          product_id: productId,
        }),
      }));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status >= 500) {
          // Service broken, not a verdict that someone else holds it: fail open.
          console.warn('Hold service error ' + response.status + ', letting add to cart through');
          return { success: true, failedOpen: true };
        }
        throw new Error(errorData.message || 'Failed to reserve product');
      }

      const data = await response.json();

      // Invalidate cache and force refresh to get the authoritative state
      await refreshCache();

      return { success: true, data };
    } catch (error) {
      if (error && (error.name === 'AbortError' || error.name === 'TypeError')) {
        // Timeout or network failure: the service did not answer, fail open.
        console.warn('Hold service unreachable, letting add to cart through:', error);
        holdApiDown = true;
        return { success: true, failedOpen: true };
      }
      console.error('Error in reserveProduct:', error);
      // Force cache refresh on error to ensure UI reflects true state
      await refreshCache().catch(err => console.error('Failed to refresh cache:', err));
      return { success: false, error: error.message || 'Failed to reserve product' };
    } finally {
      // Always remove from ongoing reservations
      ongoingReservations.delete(productId);
    }
  }


  // Function to check and show/hide expired items warning
  function updateExpiredWarning() {
    const warning = document.getElementById('expired-items-warning');
    const checkoutButton = document.getElementById('checkout');

    if (!warning) return; // Not on cart page

    const expiredCountdowns = document.querySelectorAll('.countdown[expired="true"]');
    const hasExpiredItems = !holdsOff() && expiredCountdowns.length > 0;

    if (hasExpiredItems) {
      warning.style.display = 'block';
      if (checkoutButton) {
        checkoutButton.setAttribute('disabled', true);
        checkoutButton.style.opacity = '0.5';
        checkoutButton.style.cursor = 'not-allowed';
      }
    } else {
      warning.style.display = 'none';
      if (checkoutButton) {
        checkoutButton.removeAttribute('disabled');
        checkoutButton.style.opacity = '1';
        checkoutButton.style.cursor = 'pointer';
      }
    }
  }

  // Function to display timers based on expiration times
  function displayTimer(countdownElement, expiresAt) {
    const now = new Date().getTime();
    const expiresAtTime = new Date(expiresAt).getTime();
    const timeRemaining = expiresAtTime - now;

    // Get child elements (cart page has nested structure)
    const timeSpan = countdownElement.querySelector('.countdown-time');
    const spinner = countdownElement.querySelector('.countdown-spinner');

    // Hide spinner once we have data
    if (spinner) {
      spinner.style.display = 'none';
    }

    const displayElement = timeSpan || countdownElement;

    if (timeRemaining <= 0) {
      // EXPIRED
      displayElement.textContent = "EXPIRED";
      countdownElement.setAttribute("expired", "true");
      countdownElement.style.color = "#dc2626";
      displayElement.style.color = "#dc2626";
      displayElement.style.fontWeight = "700";

      // Update the expired warning banner
      updateExpiredWarning();
    } else {
      countdownElement.removeAttribute("expired");

      const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);
      displayElement.textContent = `${minutes}m ${seconds}s`;

      // Color code based on time remaining
      let color, weight;

      if (timeRemaining < 60000) {
        // Less than 1 minute - RED
        color = "#dc2626";
        weight = "700";
      } else if (timeRemaining < 2 * 60000) {
        // Less than 2 minutes - ORANGE
        color = "#ea580c";
        weight = "700";
      } else if (timeRemaining < 5 * 60000) {
        // Less than 5 minutes - YELLOW
        color = "#ca8a04";
        weight = "600";
      } else {
        // More than 5 minutes - GREEN
        color = "#059669";
        weight = "600";
      }

      countdownElement.style.color = color;
      displayElement.style.color = color;
      displayElement.style.fontWeight = weight;

      // Update the expired warning banner (hide if no expired items)
      updateExpiredWarning();
    }
  }

  // Store active intervals globally to allow cleanup
  let activeCountdownIntervals = [];

  // Function to clear all countdown intervals
  function clearAllCountdowns() {
    activeCountdownIntervals.forEach(interval => clearInterval(interval));
    activeCountdownIntervals = [];
  }

  // Function to set up countdown intervals for each product with a timer
  function setupCountdowns(productIdsWithExpiryDate) {
    // Clear any existing intervals first
    clearAllCountdowns();

    console.log('Setting up countdowns for:', productIdsWithExpiryDate);

    productIdsWithExpiryDate.forEach(item => {
      console.log('Looking for countdown element with product ID:', item.productId, typeof item.productId);
      // Convert to string to ensure matching (Shopify IDs might be numbers or strings)
      const productIdStr = String(item.productId);
      const countdownElements = document.querySelectorAll(`.countdown[data-product-id="${productIdStr}"]`);
      console.log('Found countdown elements:', countdownElements.length);

      if (countdownElements.length === 0) {
        console.warn('No countdown element found for product:', productIdStr);
        console.log('Available countdown elements:',
          Array.from(document.querySelectorAll('.countdown')).map(el => el.dataset.productId)
        );
      }

      countdownElements.forEach(countdownElement => {
        const updateCountdown = () => {
          displayTimer(countdownElement, item.expiresAt);

          // Check if expired and trigger auto-removal after a grace period
          const now = new Date().getTime();
          const expiresAtTime = new Date(item.expiresAt).getTime();
          const timeRemaining = expiresAtTime - now;

          if (timeRemaining <= 0) {
            // Item expired - disable checkout button
            const checkoutButton = document.getElementById('checkout');
            if (checkoutButton) {
              checkoutButton.setAttribute('disabled', true);
              checkoutButton.style.opacity = '0.5';
              checkoutButton.style.cursor = 'not-allowed';
            }
          }
        };

        updateCountdown(); // Run countdown immediately
        const interval = setInterval(updateCountdown, 1000); // Update every second
        activeCountdownIntervals.push(interval);
      });
    });

    // Note: The global visibility change handler at the bottom of the file
    // will handle re-fetching data when the page becomes visible again
  }

  // Helper function to mark all countdowns as expired
  function markAllCountdownsAsExpired() {
    const countdownElements = document.querySelectorAll('.countdown');
    countdownElements.forEach(countdown => {
      const timeSpan = countdown.querySelector('.countdown-time');
      const spinner = countdown.querySelector('.countdown-spinner');
      const displayElement = timeSpan || countdown;

      if (spinner) spinner.style.display = 'none';

      displayElement.textContent = 'EXPIRED';
      countdown.setAttribute('expired', 'true');
      countdown.style.color = '#dc2626';
      displayElement.style.color = '#dc2626';
      displayElement.style.fontWeight = '700';
    });

    // Show the expired warning banner
    updateExpiredWarning();
  }

  // Main function to initialize countdowns, fetch data if needed
  async function updateCountdowns(forceFetch = false) {
    if (holdsOff()) { clearAllCountdowns(); updateExpiredWarning(); return; }
    try {
      // Use cache unless forceFetch is true
      const allCartItems = await fetchInCartIds(forceFetch);
      if (holdApiDown) {
        console.warn('Hold API unreachable - leaving countdowns as they are');
        return;
      }
      const sessionId = await getCookie('session_id');

      if (!sessionId) {
        console.warn('No session ID found');
        clearAllCountdowns();
        return;
      }

      // Only this session's holds. A missing sessionId is not ours.
      const userCartItems = allCartItems.filter(item => isOwnHold(item, sessionId));

      if (userCartItems.length > 0) {
        setupCountdowns(userCartItems);
        // Initial check for expired items after setup
        setTimeout(() => updateExpiredWarning(), 100);
      } else {
        // No items in cart - clear any existing countdowns
        clearAllCountdowns();

        // If we have countdown elements but no cart items, mark as expired
        const countdownElements = document.querySelectorAll('.countdown');
        if (countdownElements.length > 0) {
          console.warn('Cart items exist in DOM but not in backend - marking as expired');
          markAllCountdownsAsExpired();
        }
      }
    } catch (error) {
      console.error("Error initializing countdowns:", error);
      // An error is not a server answer: do not mark anything expired (fail open)
    }
  }

  async function deleteFromCart(productId) {
    if (isTestProduct(productId)) return { success: true, test: true };
    try {
      const session = await ensureHoldSession(false);

      if (!session.sessionId || !productId) {
        return { success: false, error: 'Missing session ID or product ID' };
      }

      const { response } = await holdFetch(RESERVE_API_URL, (current) => ({
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: current.sessionId,
          product_id: productId,
        }),
      }));

      if (!response.ok) {
        throw new Error('Failed to delete item from cart');
      }

      console.log("Item removed");

      // Force cache refresh after successful deletion
      await refreshCache();

      return { success: true };
    } catch (error) {
      console.error("Error deleting item from cart:", error);
      // Refresh cache even on error to sync with server state
      await refreshCache().catch(err => console.error('Failed to refresh cache:', err));
      return { success: false, error: error.message || 'Failed to delete item' };
    }
  }

  async function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null; // Return null if the cookie isn't found
  }

  // Helper function to disable a button and update its state
  function disableButton(button, text = "in another cart") {
    if (!button) return;
    button.setAttribute('disabled', true);
    button.classList.add('disabled');
    const originalText = button.textContent;
    button.textContent = text;
    button.dataset.originalText = originalText;
  }

  // Helper function to enable a button
  function enableButton(button) {
    if (!button) return;
    button.removeAttribute('disabled');
    button.classList.remove('disabled');
    if (button.dataset.originalText) {
      button.textContent = button.dataset.originalText;
      delete button.dataset.originalText;
    }
  }

  // Get all button selectors for a product
  function getProductButtons(productId) {
    const buttons = [];
    const selectors = [
      `[data-product-id="${productId}"].add-to-cart-button`,
      `[data-product-id="${productId}"].quick-add__submit`,
      `button[data-product-id="${productId}"]`
    ];

    selectors.forEach(selector => {
      const button = document.querySelector(selector);
      if (button && !buttons.includes(button)) {
        buttons.push(button);
      }
    });

    return buttons;
  }

  // Automatically sync UI with backend state periodically
  let syncInterval = null;

  function startAutoSync(intervalMs = 30000) { // Default: every 30 seconds
    if (syncInterval) {
      clearInterval(syncInterval);
    }

    syncInterval = setInterval(async () => {
      try {
        console.log("Auto-syncing cart state...");
        await refreshCache();
        await disableAddToCartFromCollectionPage();
        await updateCountdowns();
      } catch (error) {
        console.error("Auto-sync failed:", error);
      }
    }, intervalMs);
  }

  function stopAutoSync() {
    if (syncInterval) {
      clearInterval(syncInterval);
      syncInterval = null;
    }
  }

  // Listen for visibility changes to sync when user returns to tab
  document.addEventListener('visibilitychange', async () => {
    if (!document.hidden) {
      console.log("Page visible again, syncing cart state...");
      await refreshCache();

      // Only update collection page buttons if they exist
      if (document.querySelector('.quick-add__submit')) {
        await disableAddToCartFromCollectionPage();
      }

      // Only update countdowns if they exist (cart page)
      if (document.querySelector('.countdown')) {
        await updateCountdowns(true); // Force fetch after being away
      }
    }
  });

  // Strict fetch for checkout: throws on any failure instead of falling back to cache.
  async function fetchHoldsStrict() {
    const { response, session } = await holdFetch(RESERVE_API_URL, { method: "GET" });
    if (!response.ok) throw new Error("Hold API returned " + response.status);
    const data = await response.json();
    return { holds: normalizeHoldList(data, session.sessionId), sessionId: session.sessionId };
  }

  // Fails open (Florian, HAZ-139): if the hold server is down, errors or takes longer
  // than CHECKOUT_VERIFY_TIMEOUT_MS, the buyer goes to checkout. Only an actual answer
  // that a hold is missing, expired or mismatched blocks.
  async function approveCheckout(elements) {
    if (holdsOff()) return { approved: true, holdsOff: true };
    // Only ZZ TEST lines in the cart (they render no countdown): nothing to verify.
    if (document.querySelectorAll('.countdown').length === 0 && document.querySelector('.cart-item, [id^="CartItem-"]')) {
      return { approved: true, testOnly: true };
    }
    let timer;
    let result;
    try {
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error("Hold API timeout")), CHECKOUT_VERIFY_TIMEOUT_MS);
      });
      result = await Promise.race([fetchHoldsStrict(), timeout]);
    } catch (error) {
      console.warn("Hold API unavailable, letting buyer through to checkout:", error);
      holdApiDown = true;
      return { approved: true, failedOpen: true };
    } finally {
      clearTimeout(timer);
    }

    try {
      const sessionId = result.sessionId;
      const backendData = result.holds;
      holdApiDown = false;
      localStorage.setItem(cacheKey, JSON.stringify(backendData));
      localStorage.setItem(cacheExpiryKey, (Date.now() + CACHE_TTL).toString());
      const now = Date.now();

      // Only this session's reservations count. A missing sessionId is not ours.
      const ownItems = backendData.filter(item => isOwnHold(item, sessionId));
      const userItems = ownItems.filter(item => new Date(item.expiresAt).getTime() > now);

      const validProductIds = userItems.map(item => item.productId);
      const expiredProducts = ownItems.filter(item => new Date(item.expiresAt).getTime() <= now);

      const countdownElements = document.querySelectorAll(".countdown");
      const cartProductIds = Array.from(countdownElements).map(el => el.dataset.productId);

      const invalidItems = [];

      for (let element of countdownElements) {
        const productId = element.dataset.productId;
        const isExpired = expiredProducts.some(item => item.productId === productId);
        const isInBackend = validProductIds.includes(productId);

        if (isExpired || !isInBackend) {
          element.textContent = "expired";
          element.setAttribute("expired", "true");
          invalidItems.push(productId);
        }
      }

      if (invalidItems.length > 0) {
        await updateCountdowns();
        return {
          approved: false,
          error: 'Some items in your cart have expired or are no longer available',
          invalidItems
        };
      }

      // Final verification: ensure cart items match backend exactly
      const backendProductIds = userItems.map(item => item.productId).sort();
      const cartProductIdsSorted = cartProductIds.sort();

      if (JSON.stringify(backendProductIds) !== JSON.stringify(cartProductIdsSorted)) {
        console.warn("Cart mismatch detected", {
          backend: backendProductIds,
          frontend: cartProductIdsSorted
        });
        await updateCountdowns();
        return {
          approved: false,
          error: 'Cart mismatch detected. Please refresh and try again.'
        };
      }

      return { approved: true };
    } catch (error) {
      // Our own processing failed after the server answered: not a hold verdict, fail open.
      console.error("Error verifying cart items:", error);
      return { approved: true, failedOpen: true };
    }
  }
  // The warning markup ships visible (inline display:block wins). Sync it with the
  // real expired flags on load so carts without expired lines (e.g. ZZ TEST only)
  // do not show a false alert. Lines that really expire still show it.
  function syncWarningOnLoad() { try { updateExpiredWarning(); } catch (e) {} }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncWarningOnLoad);
  else syncWarningOnLoad();

  // Public API used by product-form.js, cart.js and section inline scripts
  Object.assign(window, {
    holdSessionUrl,
    ensureHoldSession,
    holdFetch,
    normalizeHoldList,
    isOwnHold,
    fetchInCartIds,
    refreshCache,
    isInCart,
    disableAddToCartFromProductPage,
    disableAddToCartFromCollectionPage,
    reserveProduct,
    updateExpiredWarning,
    displayTimer,
    clearAllCountdowns,
    setupCountdowns,
    markAllCountdownsAsExpired,
    updateCountdowns,
    deleteFromCart,
    getCookie,
    disableButton,
    enableButton,
    getProductButtons,
    isTestProduct,
    holdsOff,
    startAutoSync,
    stopAutoSync,
    approveCheckout
  });
})();
